import { X } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import { OptionSelect, type SelectOption } from '@/components/filters/OptionSelect'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { messages } from '@/lib/messages'

import type { RefOption } from './refs'

/*
 * Ayar panellerinin küçük form parçaları. Seçimler `OptionSelect` (ReportFilterBar ile aynı
 * görünüm): ref listeleri gruplu ve uzun, aranabilir seçici gerekmez.
 */

const t = messages.documents.editor

export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor?: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-muted-foreground text-xs font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  )
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
  error?: string
}) {
  const id = useId()
  return (
    <Field label={label} htmlFor={id} hint={error ? undefined : hint}>
      <Input
        id={id}
        value={value}
        aria-invalid={error ? true : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
    </Field>
  )
}

export function NumberField({
  label,
  value,
  onChange,
  min = 1,
}: {
  label: string
  value: number | undefined
  /** Boş alan `undefined` verir. */
  onChange: (value: number | undefined) => void
  min?: number
}) {
  const id = useId()
  return (
    <Field label={label} htmlFor={id}>
      <Input
        id={id}
        type="number"
        min={min}
        value={value ?? ''}
        onChange={(event) => {
          const next = event.target.valueAsNumber
          onChange(Number.isFinite(next) && next >= min ? Math.floor(next) : undefined)
        }}
      />
    </Field>
  )
}

export function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  const id = useId()
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(next) => onChange(next === true)} />
      <label htmlFor={id} className="text-sm">
        {label}
      </label>
    </div>
  )
}

export function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <Field label={label} htmlFor={id}>
      <OptionSelect id={id} value={value} options={options} onValueChange={onChange} />
    </Field>
  )
}

/** Gruplu başvuru seçici (`sorgu.ölçü`, `row.ölçü`, …). Listede olmayan değer silinmez, işaretli görünür. */
export function RefSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: RefOption[]
  onChange: (value: string) => void
}) {
  const known = options.some((option) => option.value === value)
  return (
    <SelectField
      label={label}
      value={value}
      onChange={onChange}
      options={[
        ...(known ? [] : [{ value, label: messages.memberPicker.notFound(value) }]),
        ...options.map((option) => ({
          value: option.value,
          label: option.value === option.label ? option.value : `${option.value} · ${option.label}`,
          group: option.group,
        })),
      ]}
    />
  )
}

/** Sıralama belirteçleri (`-ölçü` azalan). Üyeler verilenlerden seçilir. */
export function OrderEditor({
  label,
  value,
  members,
  onChange,
}: {
  label: string
  value: string[]
  members: string[]
  onChange: (value: string[]) => void
}) {
  const [member, setMember] = useState('')
  const [desc, setDesc] = useState(true)
  const selected = member && members.includes(member) ? member : (members[0] ?? '')
  const text = (token: string) =>
    token.startsWith('-') ? `${token.slice(1)} (${t.fields.desc})` : `${token} (${t.fields.asc})`
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-muted-foreground mb-1 text-xs font-medium">{label}</legend>
      {value.map((token) => (
        <div
          key={token}
          className="border-input flex items-center gap-1 rounded-sm border px-2 py-1 text-sm"
        >
          <span className="flex-1 truncate">{text(token)}</span>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t.fields.remove(text(token))}
            onClick={() => onChange(value.filter((item) => item !== token))}
          >
            <X />
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-1">
        <OptionSelect
          size="sm"
          aria-label={t.fields.member}
          className="min-w-0 flex-1"
          value={selected}
          options={members.map((item) => ({ value: item, label: item }))}
          onValueChange={setMember}
        />
        <OptionSelect
          size="sm"
          aria-label={t.fields.direction}
          className="w-auto"
          value={desc ? 'desc' : 'asc'}
          options={[
            { value: 'asc', label: t.fields.asc },
            { value: 'desc', label: t.fields.desc },
          ]}
          onValueChange={(next) => setDesc(next === 'desc')}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={!selected}
          onClick={() => {
            const token = `${desc ? '-' : ''}${selected}`
            const bare = selected
            onChange([...value.filter((item) => item.replace(/^-/, '') !== bare), token])
          }}
        >
          {t.fields.add}
        </Button>
      </div>
    </fieldset>
  )
}

export interface WhereItem {
  member: string
  values: string[]
}

/** Sabit eşitlik süzgeçleri: üye + virgülle ayrılmış değerler. */
export function WhereEditor({
  label,
  value,
  members,
  onChange,
}: {
  label: string
  value: WhereItem[]
  members: string[]
  onChange: (value: WhereItem[]) => void
}) {
  const [member, setMember] = useState('')
  const [text, setText] = useState('')
  const selected = member && members.includes(member) ? member : (members[0] ?? '')
  const values = text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-muted-foreground mb-1 text-xs font-medium">{label}</legend>
      {value.map((item) => {
        const summary = `${item.member} = ${item.values.join(', ')}`
        return (
          <div
            key={item.member}
            className="border-input flex items-center gap-1 rounded-sm border px-2 py-1 text-sm"
          >
            <span className="flex-1 truncate">{summary}</span>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={t.fields.remove(summary)}
              onClick={() => onChange(value.filter((other) => other !== item))}
            >
              <X />
            </Button>
          </div>
        )
      })}
      <div className="flex items-center gap-1">
        <OptionSelect
          size="sm"
          aria-label={t.fields.whereMember}
          className="min-w-0 flex-1"
          value={selected}
          options={members.map((item) => ({ value: item, label: item }))}
          onValueChange={setMember}
        />
        <Input
          aria-label={t.fields.whereValues}
          placeholder={t.fields.whereValues}
          className="h-(--control-height-sm) min-w-0 flex-1"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={!selected || values.length === 0}
          onClick={() => {
            onChange([
              ...value.filter((item) => item.member !== selected),
              { member: selected, values },
            ])
            setText('')
          }}
        >
          {t.fields.add}
        </Button>
      </div>
    </fieldset>
  )
}
